import React, { useMemo } from 'react';
import { View, Text, Pressable, Platform } from 'react-native';
import { Image } from 'expo-image';
import { BarChart3, Trophy } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { formatWorldCupRankBadge } from '../../../src/worldcup/helpers/worldCupGroupsDisplay.helper';
import { getWorldCupMemberAvatarSource } from '../../../src/worldcup/helpers/worldCupMemberAvatarSource.helper';
import { AvatarGroup } from '../../shared/AvatarGroup/AvatarGroup';
import { AVATAR_GROUP_DEFAULTS } from '../../shared/AvatarGroup/AvatarGroup.constants';
import { GroupsViewMemberStack } from './GroupsViewMemberStack';
import { GroupsViewCardPrize } from './GroupsViewCardPrize';
import { getGroupsViewCardStyles } from './GroupsViewCard.styles';
import {
  getGroupsViewGroupCategory,
  GROUPS_VIEW_COPY,
  GROUPS_VIEW_RANK_GOLD_BADGE_MAX,
} from './GroupsView.constants';
import type { WorldCupGroup } from '../../../src/worldcup/types/worldcup.types';

interface GroupsViewCardProps {
  group: WorldCupGroup;
  onPress: (groupId: string) => void;
}

export const GroupsViewCard: React.FC<GroupsViewCardProps> = ({ group, onPress }) => {
  const theme = useTheme();
  const styles = useMemo(() => getGroupsViewCardStyles(theme), [theme]);

  const rankBadge = formatWorldCupRankBadge(group.userRank);
  const isGoldBadge = group.userRank <= GROUPS_VIEW_RANK_GOLD_BADGE_MAX;
  const showTrophy = group.userRank <= 3 && isGoldBadge;
  const ringColor = theme.background.card;

  const avatarItems = useMemo(() => {
    const prev = group.memberPreview;
    if (!prev || prev.length === 0) return [];
    return prev.map((m) => ({
      source: getWorldCupMemberAvatarSource(m.avatarUrl, m.avatarId),
      avatarConfig: m.avatarConfig ?? null,
      label: m.displayName,
    }));
  }, [group.memberPreview]);

  return (
    <Pressable
      style={({ pressed, hovered }) => [
        styles.groupCard,
        Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
        pressed && styles.groupCardPressed,
        hovered && Platform.OS === 'web' && styles.groupCardHover,
      ]}
      onPress={() => onPress(group.id)}
    >
      {Platform.OS === 'web' ? <View style={styles.groupCardGlow} pointerEvents="none" /> : null}
      <View style={styles.groupCardTopRow}>
        <View style={styles.groupCardTitleBlock}>
          {group.companyBranding?.logoUrl ? (
            <View style={styles.companyTitleRow}>
              <Image
                source={{ uri: group.companyBranding.logoUrl }}
                style={styles.companyLogoLarge}
                contentFit="contain"
                alt={group.companyBranding.companyName}
              />
              <Text
                style={[styles.groupName, styles.groupNameWithLogo]}
                numberOfLines={2}
              >
                {group.name}
              </Text>
            </View>
          ) : (
            <>
              <Text
                style={styles.categoryLabel}
                numberOfLines={1}
              >
                {group.companyBranding
                  ? GROUPS_VIEW_COPY.COMPANY_GROUP_LABEL
                  : getGroupsViewGroupCategory()}
              </Text>
              <Text
                style={styles.groupName}
                numberOfLines={2}
              >
                {group.name}
              </Text>
            </>
          )}
        </View>
        <View
          style={[
            styles.rankBadge,
            isGoldBadge ? styles.rankBadgeGold : styles.rankBadgeNeutral,
          ]}
        >
          {isGoldBadge ? (
            <Trophy size={16} color={theme.onPrimary} fill={showTrophy ? theme.onPrimary : 'none'} />
          ) : (
            <BarChart3 size={16} color={theme.black} />
          )}
          <Text
            style={isGoldBadge ? styles.rankBadgeTextGold : styles.rankBadgeTextNeutral}
          >
            {rankBadge}
          </Text>
        </View>
      </View>
      <View style={styles.groupCardBottomRow}>
        {avatarItems.length > 0 ? (
          <AvatarGroup
            avatars={avatarItems}
            totalMemberCount={group.memberCount}
            maxVisible={AVATAR_GROUP_DEFAULTS.MAX_VISIBLE}
            size={AVATAR_GROUP_DEFAULTS.SIZE}
            overlap={AVATAR_GROUP_DEFAULTS.OVERLAP}
            ringColor={ringColor}
            keyPrefix={group.id}
          />
        ) : (
          <GroupsViewMemberStack
            memberCount={group.memberCount}
            styles={styles}
            isRecessed={false}
          />
        )}
        <GroupsViewCardPrize group={group} styles={styles} />
      </View>
    </Pressable>
  );
};
